import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { TokenPurpose } from "@/enum";
import AppLogo from "@/assets/images/favicon.png";

import { LoadingOverlay } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { SetPasswordType } from "./schema/set-password";
import {
	getSetPasswordRedirectPath,
	getSetPasswordTokenError,
	resolveLinkProblem,
} from "./utils/set-password-response";
import { InvitationLinkNotice } from "./components/InvitationLinkNotice";
import {
	PASSWORD_COMPLEXITY_REQUIRED_SCORE,
	PasswordComplexityField,
	usePasswordComplexity,
} from "./components/PasswordComplexityField";

import { useSetPasswordMutation, useValidateInvitationTokenQuery } from "../../redux/auth.api";

export const SetPassword: FunctionComponent = () => {
	const { t } = useTranslation();
	const [password, setPassword] = useState("");
	const [tokenStatusError, setTokenStatusError] = useState<ERROR_TYPE | null>(null);

	const passwordComplexity = usePasswordComplexity(password);

	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const token = searchParams.get("token") || "";

	const { data: tokenValidation, isLoading: isValidatingToken } = useValidateInvitationTokenQuery(
		token,
		{ skip: !token }
	);

	const [submitPassword, { isLoading, error }] = useSetPasswordMutation();

	const onSubmit = async (values: SetPasswordType) => {
		try {
			setTokenStatusError(null);

			const response = await submitPassword({
				token,
				purpose: TokenPurpose.INVITATION,
				password: values.password,
			}).unwrap();

			const redirectPath = getSetPasswordRedirectPath(response);

			if (redirectPath) {
				navigate(redirectPath);

				return;
			}

			setTokenStatusError(getSetPasswordTokenError(response, "invitation"));
		} catch (err) {
			console.error(err);
		}
	};

	const passwordErrors = getAllErrors(error, tokenStatusError ? [tokenStatusError] : []);

	if (token && isValidatingToken) {
		return <LoadingOverlay />;
	}

	// A missing token or any non-OPEN status (EXPIRED / USED / INVALID) is surfaced up front via the
	// notice instead of letting the user submit a password that will fail.
	const linkProblem = resolveLinkProblem(token, tokenValidation?.status);

	if (linkProblem) {
		return <InvitationLinkNotice status={linkProblem} token={token} />;
	}

	return (
		<div className={`w-full h-full flex flex-row items-center justify-center `}>
			<div className="flex flex-col bg-white p-6 rounded-[20px] w-[428px] max-w-[100%] shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)] items-center justify-center">
				<img alt="logo" className="w-[48px] h-[48px] mb-4" src={AppLogo} />
				<div className="text-center text-md font-bold mb-4">{t("setPassword.inviteTitle")}</div>
				<div className="text-center text-ssm font-normal mb-8">
					{t("setPassword.inviteSubtitle")}
				</div>
				<PasswordComplexityField
					complexity={passwordComplexity}
					label={t("setPassword.passwordRule")}
					password={password}
					placeholder={t("setPassword.setPlaceholder")}
					onChange={setPassword}
					onEnter={() => onSubmit({ password })}
				/>
				{passwordErrors.length ? (
					<div className="w-full mb-4">{renderErrorMessages(passwordErrors)}</div>
				) : null}
				<div className="w-full">
					<Button
						disabled={passwordComplexity < PASSWORD_COMPLEXITY_REQUIRED_SCORE}
						isLoading={isLoading}
						size={ButtonSize.ML}
						title={t("setPassword.submit")}
						onClick={() => onSubmit({ password })}
					/>
				</div>
			</div>
		</div>
	);
};
